import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Users, Layers, Award, Globe, Upload, Search, Loader2, Lock, ArrowRight, X } from "lucide-react";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4", "#EF4444", "#84CC16"];

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="label-mono text-slate-500">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
      </div>
      <div className="font-display text-3xl font-bold">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState(null);

  const load = async (reg = region) => {
    try {
      const { data } = await api.get("/dashboard/stats", { params: reg ? { region: reg } : {} });
      setStats(data);
      if (data.has_data && data.status === "processing") setTimeout(() => load(reg), 2500);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(null); }, []);

  const selectRegion = (reg) => {
    const next = region === reg ? null : reg;
    setRegion(next);
    load(next);
  };

  if (loading) {
    return <AppShell><div className="flex items-center gap-3 text-slate-400 py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading overview…</div></AppShell>;
  }

  if (!stats?.has_data) {
    return (
      <AppShell>
        <div className="max-w-lg mx-auto text-center py-16 animate-fade-up">
          <div className="w-16 h-16 rounded-2xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center mx-auto mb-6"><Upload className="w-8 h-8 text-blue-400" /></div>
          <h1 className="font-display text-2xl font-semibold mb-2">No network imported yet</h1>
          <p className="text-slate-400 mb-8">Upload your contacts file (CSV, Excel or ZIP) to unlock your intelligence dashboard.</p>
          <Link to="/upload" data-testid="empty-upload-cta" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-lg font-medium transition-colors">Import connections <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </AppShell>
    );
  }

  const processing = stats.status === "processing";
  const pct = stats.enrich_limit ? Math.round((stats.enriched_count / stats.enrich_limit) * 100) : 0;

  return (
    <AppShell>
      <div className="space-y-6 lg:space-y-8 animate-fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Network Overview</h1>
            <p className="text-slate-500 text-sm mt-1 font-mono-niq">{stats.filename}</p>
          </div>
          <div className="flex gap-2">
            <Link to={region ? `/search?region=${encodeURIComponent(region)}` : "/search"} className="flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-lg font-medium transition-colors"><Search className="w-4 h-4" /> Ask your network</Link>
          </div>
        </div>

        {processing && (
          <div className="rounded-xl border border-blue-500/25 bg-blue-500/[0.06] p-4 flex items-center gap-4">
            <Loader2 className="w-5 h-5 text-blue-400 animate-spin shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium mb-1.5">Enriching contacts… {stats.enriched_count} / {stats.enrich_limit}</div>
              <div className="h-1.5 rounded-full bg-[#1E2A3F] overflow-hidden"><div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
            </div>
          </div>
        )}

        {stats.locked > 0 && (
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4 flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-400 shrink-0" />
            <div className="flex-1 text-sm text-slate-300"><span className="font-medium text-amber-300">{stats.locked} contacts locked.</span> The free plan enriches your first 50 connections. Upgrade to Pro to unlock your full network.</div>
            <Link to="/billing" className="text-xs bg-amber-500/15 border border-amber-500/30 text-amber-300 px-3 py-2 rounded-lg hover:bg-amber-500/25 transition-colors whitespace-nowrap">Upgrade</Link>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total contacts" value={stats.total_contacts.toLocaleString()} accent="bg-blue-600/15 text-blue-400" />
          <StatCard icon={Award} label="Enriched" value={stats.enriched_count.toLocaleString()} sub="AI classified" accent="bg-emerald-600/15 text-emerald-400" />
          <StatCard icon={Layers} label="Sectors" value={stats.by_sector.length} accent="bg-violet-600/15 text-violet-400" />
          <StatCard icon={Globe} label="Regions" value={stats.by_region.length} accent="bg-amber-600/15 text-amber-400" />
        </div>

        <div className="grid lg:grid-cols-3 gap-4 lg:gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-[#27334D] bg-[#111726] p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="font-display text-lg font-semibold">By sector</h3>
              {region && (
                <button data-testid="clear-region-filter" onClick={() => selectRegion(region)} className="flex items-center gap-1.5 text-xs bg-blue-600/15 border border-blue-500/30 text-blue-300 px-2.5 py-1 rounded-full hover:bg-blue-600/25 transition-colors">
                  {region} · {stats.region_count?.toLocaleString()} <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-5 font-mono-niq">{region ? `Sectors in ${region}` : "Inferred industry distribution"}</p>
            {stats.by_sector.length === 0 ? (
              <div className="flex items-center justify-center h-[280px] text-sm text-slate-500">No enriched contacts in this region yet.</div>
            ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.by_sector.slice(0, 8)} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="label" width={110} tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(59,130,246,0.08)" }} contentStyle={{ background: "#1A2234", border: "1px solid #27334D", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {stats.by_sector.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>

          <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-5 sm:p-6">
            <h3 className="font-display text-lg font-semibold mb-1">By seniority</h3>
            <p className="text-xs text-slate-500 mb-5 font-mono-niq">{region ? `Levels in ${region}` : "Inferred level"}</p>
            {stats.by_seniority.length === 0 ? (
              <div className="flex items-center justify-center h-[200px] text-sm text-slate-500">No data for this region.</div>
            ) : (<>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={stats.by_seniority} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {stats.by_seniority.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#111726" strokeWidth={2} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#1A2234", border: "1px solid #27334D", borderRadius: 8, color: "#fff", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {stats.by_seniority.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 text-slate-300"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} /> {s.label}</span>
                  <span className="text-slate-500 font-mono-niq">{s.count}</span>
                </div>
              ))}
            </div>
            </>)}
          </div>
        </div>

        <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">By region</h3>
            <span className="text-xs text-slate-500 font-mono-niq">Click a region to filter the charts above</span>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button
              data-testid="region-chip-all"
              onClick={() => region && selectRegion(region)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${!region ? "border-blue-500 bg-blue-600/20 text-blue-200" : "border-[#27334D] bg-[#0D121C] hover:border-blue-500/50 hover:bg-[#141c2b]"}`}
            >
              <Globe className={`w-3.5 h-3.5 ${!region ? "text-blue-300" : "text-slate-500"}`} /> All regions
              <span className={`font-mono-niq text-xs ${!region ? "text-blue-300" : "text-slate-500"}`}>{stats.enriched_count.toLocaleString()}</span>
            </button>
            {stats.by_region.map((r, i) => {
              const active = region === r.label;
              return (
                <button
                  key={i}
                  data-testid={`region-chip-${r.label}`}
                  onClick={() => selectRegion(r.label)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${active ? "border-blue-500 bg-blue-600/20 text-blue-200" : "border-[#27334D] bg-[#0D121C] hover:border-blue-500/50 hover:bg-[#141c2b]"}`}
                >
                  <Globe className={`w-3.5 h-3.5 ${active ? "text-blue-300" : "text-slate-500"}`} /> {r.label}
                  <span className={`font-mono-niq text-xs ${active ? "text-blue-300" : "text-slate-500"}`}>{r.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
